<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useField } from 'vee-validate'
import { useResponsiveSize, getLocalizedText } from '../utils/formHelpers'
import type { FormField } from '../types/formTypeStructure'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import { AlertCircle } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'password' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

const props = defineProps<Props>()

const fieldName = props.name ?? props.field.fieldName.toString()
const { calculateSize } = useResponsiveSize()
const { width, height, borderRadius } = calculateSize(window.innerWidth)
const placeholder = getLocalizedText(props.field.placeholder, props.currentLang ?? 'fr')
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const inputRef = ref<HTMLInputElement | null>(null)

const {
  value,
  errorMessage,
  handleChange,
  setTouched,
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

const inputClass = computed(() => [
  'w-full',
  'text-[#04406E]',
  'rounded',
  'outline-none',
  'transition-all',
  'shadow-sm',
  'focus:ring-2',
  'focus:ring-[#04406E]',
  'border',
  'border-[#04406E]',
  'bg-[#f7fafb]',
  props.className
])
</script>

<template>
  <div class="password-field-container">
    <!-- Label dupliqué au-dessus de l'input password -->
    <div class="password-field-label-container">
      <span class="password-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <input
        ref="inputRef"
        type="password"
        v-model="value"
        :name="fieldName"
        :placeholder="placeholder"
        :style="{
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: `${borderRadius}px`,
          padding: '8px 12px',
          fontSize: '1rem'
        }"
        :class="inputClass"
        @input="handleChange"
        @blur="() => setTouched(true)"
      />

      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.password-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.password-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.password-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>
